import { LegalPage, LegalSection, LegalToc } from './LegalPage';
import { PageLink, ROUTES } from '../router';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { BUSINESS_DASHBOARD_URL } from '../data/navigation';
import { INVITE_EXPIRY_DAYS, SUPPORT_EMAIL } from '../data/legal';
import './TeamGuidePage.css';

const TEAM_PAGE_URL = `${BUSINESS_DASHBOARD_URL}/dashboard/team`;

/**
 * The message admins are asked to send their drivers. The dashboard's "Copy
 * message" button (tulink-backend apps/dashboard team/invite-onboarding.tsx)
 * uses the same wording with the organisation's name in place of "our
 * organisation", so the two stay recognisable.
 */
export const DRIVER_MESSAGE =
  'Hi! I have invited you to our organisation on Tulink. Update the Tulink app, ' +
  'open the Invites tab and tap Review on the organisation invite to join. ' +
  `The invite expires in ${INVITE_EXPIRY_DAYS} days.`;

const INVITE_STEPS = [
  {
    title: 'Find them',
    body: 'On the Team page, search by the name or email address they use in the Tulink app. They need a Tulink account first.',
  },
  {
    title: 'Send the invite',
    body: 'Tap Invite. Tulink tries to send them a notification, and the invite appears in the Invitations list as Pending.',
  },
  {
    title: 'They accept in the app',
    body: 'In the app they open the Invites tab, review what joining means and tap Join. You get an email when they accept or decline.',
  },
  {
    title: 'They join your team',
    body: 'Once they accept they move to your members list, and the journeys they lead or join start appearing on your dashboard.',
  },
];

const STATUSES = [
  {
    term: 'Pending',
    detail: `Waiting for them to answer in the app. An invite can be accepted for ${INVITE_EXPIRY_DAYS} days. You can revoke it while it is pending.`,
  },
  {
    term: 'Expired',
    detail: `Nobody answered within ${INVITE_EXPIRY_DAYS} days. Use Invite again to send a fresh one.`,
  },
  {
    term: 'Declined',
    detail: 'They chose not to join. You can invite them again if they change their mind.',
  },
  {
    term: 'Member',
    detail: 'They accepted. They appear in the members list, where you can assign them to operators or remove them.',
  },
];

const PROBLEMS = [
  {
    term: 'They did not get a notification',
    detail:
      'Notifications are not guaranteed: they can be switched off, or the phone may be offline. The invite still waits in the app. Ask them to update Tulink and open the Invites tab.',
  },
  {
    term: '“This Tulink user can’t be invited right now”',
    detail: `They already have an invite waiting from another organisation. It can stay open for up to ${INVITE_EXPIRY_DAYS} days; try again once they have answered it or it has expired.`,
  },
  {
    term: '“Tulink user already belongs to another organization”',
    detail:
      'An app account can belong to one organisation at a time. They can leave the other organisation from their profile in the app, or its admin can remove them. Then invite them again.',
  },
  {
    term: '“Tulink user is already a member of this organization”',
    detail: 'They are already on your team. Look for them in the members list.',
  },
  {
    term: 'You cannot find them in search',
    detail:
      'Search matches the name or email on their Tulink account. Check they have signed up in the app and try their email address.',
  },
];

const SECTIONS = [
  { id: 'two-kinds-of-people', title: 'Two kinds of people on your team' },
  { id: 'invite-app-members', title: 'Invite app members' },
  { id: 'invite-statuses', title: 'What each status means' },
  { id: 'tell-your-drivers', title: 'Tell your drivers' },
  { id: 'what-you-can-see', title: 'What your organisation can see' },
  { id: 'removing-and-leaving', title: 'Removing members and leaving' },
  { id: 'troubleshooting', title: 'Troubleshooting' },
];

const Definitions = ({ items }) => (
  <dl className="tl-legal__providers">
    {items.map((item) => (
      <div key={item.term}>
        <dt>{item.term}</dt>
        <dd>{item.detail}</dd>
      </div>
    ))}
  </dl>
);

/**
 * Admin guide for building a team in the Tulink business dashboard: who is
 * who, how app-member invites work, and what members agree to share.
 *
 * Every statement describes behaviour in the backend and dashboard; keep it in
 * step with docs/api/modules/organization.md in tulink-backend.
 */
export const TeamGuidePage = () => {
  useDocumentTitle('Add your team — Tulink for business');

  return (
    <LegalPage
      eyebrow="Tulink for business · Guide"
      title="Add your team"
      lede="Bring your dashboard operators and your drivers into one organisation, and understand what each of them sees."
      aside={<LegalToc sections={SECTIONS} />}
    >
      <LegalSection id="two-kinds-of-people" title="Two kinds of people on your team">
        <p>
          The Team page in the dashboard lists two different groups. Both are invited, but in
          different ways.
        </p>
        <h3>Dashboard operators</h3>
        <p>
          The people who use the dashboard to follow journeys, read reports and manage the team.
          You invite them by email from <strong>Invite or change roles</strong> on the Team page.
          Admins see every journey in the organisation and can manage the team. Other operators see
          only the app members an admin has assigned to them.
        </p>
        <h3>App members</h3>
        <p>
          The drivers and travellers who use the Tulink app on their phones. You invite them from
          the Team page, and they decide in the app whether to join. Their journeys only appear on
          your dashboard after they accept.
        </p>
      </LegalSection>

      <LegalSection id="invite-app-members" title="Invite app members">
        <ol className="tl-guide__steps" aria-label="Inviting an app member">
          {INVITE_STEPS.map((step, index) => (
            <li key={step.title} className="tl-guide__step">
              <span className="tl-guide__step-number" aria-hidden="true">
                {index + 1}
              </span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <p>
          <a
            className="tl-button tl-button--primary tl-guide__action"
            href={TEAM_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the Team page
          </a>
        </p>
        <p>
          People who were added to your team before invitations existed stay members. Nothing
          changes for them.
        </p>
      </LegalSection>

      <LegalSection id="invite-statuses" title="What each status means">
        <Definitions items={STATUSES} />
      </LegalSection>

      <LegalSection id="tell-your-drivers" title="Tell your drivers">
        <p>
          The quickest way to get people on board is to tell them the invite is coming. Under{' '}
          <strong>How invites work</strong> on the Team page, <strong>Copy message</strong> copies a
          message like this, with your organisation’s name, ready to paste into WhatsApp or a text:
        </p>
        <blockquote className="tl-guide__message">{DRIVER_MESSAGE}</blockquote>
        <p>
          Before they join, the app shows them what your organisation will be able to see, and they
          can leave at any time from their profile.
        </p>
      </LegalSection>

      <LegalSection id="what-you-can-see" title="What your organisation can see">
        <p>Once someone accepts, your organisation’s operators can see:</p>
        <ul>
          <li>their name and email address in the members list;</li>
          <li>
            the journeys run under your organisation, including where each of your members is
            while a journey is active, and the journey history and reports afterwards;
          </li>
          <li>
            your members’ positions on journeys started by someone outside your organisation. You
            see only your own members on those journeys, not everyone else in the group.
          </li>
        </ul>
        <p>
          Location is only shared during a journey. Nobody, including your organisation, can see a
          member’s location between journeys. Members can also record routes and save them for
          everyone in your organisation to use.
        </p>
        <p>
          Members are told all of this before they join. The full detail is in the{' '}
          <PageLink to={ROUTES.privacy}>Tulink Privacy Policy</PageLink>.
        </p>
      </LegalSection>

      <LegalSection id="removing-and-leaving" title="Removing members and leaving">
        <p>
          An admin can remove an app member from the Team page. Tulink tries to send them a
          notification saying so, and the organisation’s features, such as saved routes, disappear
          from their app.
        </p>
        <p>
          Members can leave your organisation themselves from their profile in the app. Either way,
          journeys that were already run under your organisation stay in its history; journeys
          after that point do not.
        </p>
      </LegalSection>

      <LegalSection id="troubleshooting" title="Troubleshooting">
        <Definitions items={PROBLEMS} />
        <p>
          Still stuck? Write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </LegalSection>
    </LegalPage>
  );
};
